import * as Sentry from '@sentry/react';
import React, { Suspense, lazy } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import { ImpersonationBanner } from '@/components/auth/ImpersonationBanner';
import { ReadOnlyNotice } from '@/components/auth/ReadOnlyNotice';
import { PageErrorBoundary } from '@/components/error';
import { PaywallProvider } from '@/components/paywall';
import { AuthSessionProvider } from '@/components/providers';
import { AuthRoute, ProtectedRoute } from '@/components/routing';
import { CircularProgress } from '@/components/ui';
import { PublicLayout, AuthenticatedLayout } from '@/layouts';
import { NotFoundPage } from '@/pages/NotFound';
import {
  PUBLIC_ROUTES,
  PROTECTED_ROUTES,
  PLANS_ROUTES,
  WORKOUTS_ROUTES,
  SESSIONS_ROUTES,
  EXERCISES_ROUTES,
  CHALLENGES_ROUTES,
  CLIENTS_ROUTES,
  PROPOSALS_ROUTES,
  GOALS_ROUTES,
  MEASUREMENTS_ROUTES,
  PROFILE_ROUTES,
  QUESTIONNAIRE_ROUTES,
  toRelativePath,
} from '@/utils';

// Lazy-loaded public page components for code splitting
const LandingPage = lazy(() => import('@/pages/public/LandingPage'));
const LoginPage = lazy(() => import('@/pages/public/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/public/RegisterPage'));
const ForgotPasswordPage = lazy(
  () => import('@/pages/public/ForgotPasswordPage')
);
const ResetPasswordPage = lazy(
  () => import('@/pages/public/ResetPasswordPage')
);
const PricingPage = lazy(() => import('@/pages/public/PricingPage'));
const AboutPage = lazy(() => import('@/pages/public/AboutPage'));
const ContactPage = lazy(() => import('@/pages/public/ContactPage'));
const FAQPage = lazy(() => import('@/pages/public/FAQPage'));
const HelpPage = lazy(() => import('@/pages/public/HelpPage'));
const MobileAppPage = lazy(() => import('@/pages/public/MobileAppPage'));
const PrivacyPage = lazy(() => import('@/pages/public/PrivacyPage'));
const TermsPage = lazy(() => import('@/pages/public/TermsPage'));
const AccountDeletedPage = lazy(
  () => import('@/pages/public/AccountDeletedPage')
);
const EmailChangePage = lazy(() => import('@/pages/public/EmailChangePage'));
const VerifyEmailPage = lazy(() => import('@/pages/public/VerifyEmailPage'));

/**
 * `lazy()` for a page exported by name. Each module becomes its own chunk,
 * fetched on first navigation to the route; the entry bundle carries only the
 * shell. Pages that export a default go through `lazy()` directly above.
 */
const lazyPage = <M extends Record<K, React.ComponentType>, K extends keyof M>(
  load: () => Promise<M>,
  name: K
): React.LazyExoticComponent<M[K]> =>
  lazy(() => load().then((module) => ({ default: module[name] })));

// Authenticated pages
const DashboardPage = lazyPage(
  () => import('@/pages/Dashboard/DashboardPage'),
  'DashboardPage'
);
const ReportsPage = lazyPage(
  () => import('@/pages/Reports/ReportsPage'),
  'ReportsPage'
);
const CalendarPage = lazyPage(
  () => import('@/pages/Calendar/CalendarPage'),
  'CalendarPage'
);
const FeedPage = lazyPage(() => import('@/pages/Feed/FeedPage'), 'FeedPage');
const StandingPage = lazyPage(
  () => import('@/pages/Standing/StandingPage'),
  'StandingPage'
);
const FriendsPage = lazyPage(
  () => import('@/pages/Friends/FriendsPage'),
  'FriendsPage'
);
const TrainersPage = lazyPage(
  () => import('@/pages/Trainers/TrainersPage'),
  'TrainersPage'
);
const MeasurementsPage = lazyPage(
  () => import('@/pages/Measurements/MeasurementsPage'),
  'MeasurementsPage'
);
const MeasurementMetricPage = lazyPage(
  () => import('@/pages/Measurements/MeasurementMetricPage'),
  'MeasurementMetricPage'
);
const MeasurementFormPage = lazyPage(
  () => import('@/pages/Measurements/MeasurementFormPage'),
  'MeasurementFormPage'
);
const MeasurementEntryPage = lazyPage(
  () => import('@/pages/Measurements/MeasurementEntryPage'),
  'MeasurementEntryPage'
);
const PlansPage = lazyPage(
  () => import('@/pages/Plans/PlansPage'),
  'PlansPage'
);
const PlanViewPage = lazyPage(
  () => import('@/pages/Plans/PlanViewPage'),
  'PlanViewPage'
);
const PlanSchedulePage = lazyPage(
  () => import('@/pages/Plans/PlanSchedulePage'),
  'PlanSchedulePage'
);
const PlanSharePage = lazyPage(
  () => import('@/pages/Plans/PlanSharePage'),
  'PlanSharePage'
);
const PlanFormPage = lazyPage(
  () => import('@/pages/Plans/PlanFormPage'),
  'PlanFormPage'
);
const WorkoutsPage = lazyPage(
  () => import('@/pages/Workouts/WorkoutsPage'),
  'WorkoutsPage'
);
const WorkoutViewPage = lazyPage(
  () => import('@/pages/Workouts/WorkoutViewPage'),
  'WorkoutViewPage'
);
const SessionSummaryPage = lazyPage(
  () => import('@/pages/Sessions/SessionSummaryPage'),
  'SessionSummaryPage'
);
const SessionComparisonPage = lazyPage(
  () => import('@/pages/Sessions/SessionComparisonPage'),
  'SessionComparisonPage'
);
const WorkoutFormPage = lazyPage(
  () => import('@/pages/Workouts/WorkoutFormPage'),
  'WorkoutFormPage'
);
const WorkoutSessionPage = lazyPage(
  () => import('@/pages/Workouts/WorkoutSessionPage'),
  'WorkoutSessionPage'
);
const ExercisesPage = lazyPage(
  () => import('@/pages/Exercises/ExercisesPage'),
  'ExercisesPage'
);
const ExerciseViewPage = lazyPage(
  () => import('@/pages/Exercises/ExerciseViewPage'),
  'ExerciseViewPage'
);
const CustomExerciseFormPage = lazyPage(
  () => import('@/pages/Exercises/CustomExerciseFormPage'),
  'CustomExerciseFormPage'
);
const ChallengesPage = lazyPage(
  () => import('@/pages/Challenges/ChallengesPage'),
  'ChallengesPage'
);
const ChangeProposalPage = lazyPage(
  () => import('@/pages/ChangeProposal/ChangeProposalPage'),
  'ChangeProposalPage'
);
const ClientsPage = lazyPage(
  () => import('@/pages/Clients/ClientsPage'),
  'ClientsPage'
);
const ClientViewPage = lazyPage(
  () => import('@/pages/Clients/ClientViewPage'),
  'ClientViewPage'
);
const GoalFormPage = lazyPage(
  () => import('@/pages/Goals/GoalFormPage'),
  'GoalFormPage'
);
const GoalsPage = lazyPage(
  () => import('@/pages/Goals/GoalsPage'),
  'GoalsPage'
);
const GoalViewPage = lazyPage(
  () => import('@/pages/Goals/GoalViewPage'),
  'GoalViewPage'
);
const ChallengeFormPage = lazyPage(
  () => import('@/pages/Challenges/ChallengeFormPage'),
  'ChallengeFormPage'
);
const ChallengeViewPage = lazyPage(
  () => import('@/pages/Challenges/ChallengeViewPage'),
  'ChallengeViewPage'
);
const NotificationsPage = lazyPage(
  () => import('@/pages/Notifications/NotificationsPage'),
  'NotificationsPage'
);
const SettingsPage = lazyPage(
  () => import('@/pages/Settings/SettingsPage'),
  'SettingsPage'
);
const ProfilePage = lazyPage(
  () => import('@/pages/Profile/ProfilePage'),
  'ProfilePage'
);
const QuestionnairePage = lazyPage(
  () => import('@/pages/Questionnaire/QuestionnairePage'),
  'QuestionnairePage'
);

/**
 * Minimal centered loading spinner for lazy-loaded page fallback.
 * Shown briefly while page chunks are being loaded.
 */
const PageLoadingFallback: React.FC = () => (
  <div className="flex min-h-[50vh] items-center justify-center">
    <CircularProgress size="lg" aria-label="Loading page" />
  </div>
);

// Wrapper for every page: an error boundary around the Suspense that shows
// the fallback while the page's chunk loads. A chunk that fails to load
// throws into the boundary like any other page error.
const PageWithErrorBoundary: React.FC<{
  children: React.ReactNode;
  pageName: string;
}> = ({ children, pageName }) => {
  return (
    <PageErrorBoundary
      pageName={pageName}
      onError={(error, errorInfo) => {
        console.error(`Error in ${pageName}:`, error, errorInfo);
      }}
    >
      <Suspense fallback={<PageLoadingFallback />}>{children}</Suspense>
    </PageErrorBoundary>
  );
};

// Wrapped so Sentry's tracing names each navigation after its route pattern.
const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

// Create the router configuration
export const router = sentryCreateBrowserRouter([
  // Public routes
  {
    path: '/',
    element: <PublicLayout />,
    children: [
      {
        index: true,
        element: (
          <PageWithErrorBoundary pageName="Landing">
            <LandingPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: 'login',
        element: (
          <PageWithErrorBoundary pageName="Login">
            <AuthRoute>
              <LoginPage />
            </AuthRoute>
          </PageWithErrorBoundary>
        ),
      },
      {
        path: 'register',
        element: (
          <PageWithErrorBoundary pageName="Register">
            <AuthRoute>
              <RegisterPage />
            </AuthRoute>
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(PUBLIC_ROUTES.FORGOT_PASSWORD),
        element: (
          <PageWithErrorBoundary pageName="ForgotPassword">
            <AuthRoute>
              <ForgotPasswordPage />
            </AuthRoute>
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(PUBLIC_ROUTES.RESET_PASSWORD),
        element: (
          <PageWithErrorBoundary pageName="ResetPassword">
            <AuthRoute>
              <ResetPasswordPage />
            </AuthRoute>
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(PUBLIC_ROUTES.PRICING),
        element: (
          <PageWithErrorBoundary pageName="Pricing">
            <PricingPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(PUBLIC_ROUTES.ABOUT),
        element: (
          <PageWithErrorBoundary pageName="About">
            <AboutPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(PUBLIC_ROUTES.CONTACT),
        element: (
          <PageWithErrorBoundary pageName="Contact">
            <ContactPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(PUBLIC_ROUTES.FAQ),
        element: (
          <PageWithErrorBoundary pageName="FAQ">
            <FAQPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(PUBLIC_ROUTES.HELP),
        element: (
          <PageWithErrorBoundary pageName="Help">
            <HelpPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(PUBLIC_ROUTES.MOBILE_APP),
        element: (
          <PageWithErrorBoundary pageName="Mobile App">
            <MobileAppPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(PUBLIC_ROUTES.PRIVACY),
        element: (
          <PageWithErrorBoundary pageName="Privacy">
            <PrivacyPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(PUBLIC_ROUTES.TERMS),
        element: (
          <PageWithErrorBoundary pageName="Terms">
            <TermsPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        // Public, unguarded — reached only after the session is already gone.
        path: toRelativePath(PUBLIC_ROUTES.ACCOUNT_DELETED),
        element: (
          <PageWithErrorBoundary pageName="Account Deleted">
            <AccountDeletedPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        // Public, unguarded: an emailed link opens wherever its mail is read,
        // signed in or not.
        path: toRelativePath(PUBLIC_ROUTES.EMAIL_CHANGE),
        element: (
          <PageWithErrorBoundary pageName="Email Change">
            <EmailChangePage />
          </PageWithErrorBoundary>
        ),
      },
      {
        // Public, unguarded, like the email change: a verification link opens
        // wherever its mail is read, and the page itself asks a signed-out
        // visitor to log in.
        path: toRelativePath(PUBLIC_ROUTES.VERIFY_EMAIL),
        element: (
          <PageWithErrorBoundary pageName="Verify Email">
            <VerifyEmailPage />
          </PageWithErrorBoundary>
        ),
      },
    ],
  },
  // Live workout session — authenticated but SHELL-LESS (focus mode, no
  // sidebar/header), so it sits beside AuthenticatedLayout rather than inside
  // it and re-mounts the providers the shell would otherwise supply.
  {
    path: WORKOUTS_ROUTES.SESSION,
    element: (
      <PageWithErrorBoundary pageName="Workout Session">
        <ProtectedRoute>
          <AuthSessionProvider>
            <PaywallProvider>
              {/* In flow, not fixed: this page scrolls the window, and has no
                  fixed chrome to offset. */}
              <ImpersonationBanner />
              <ReadOnlyNotice />
              <WorkoutSessionPage />
            </PaywallProvider>
          </AuthSessionProvider>
        </ProtectedRoute>
      </PageWithErrorBoundary>
    ),
  },
  // Protected routes
  {
    path: '/',
    element: <AuthenticatedLayout />,
    children: [
      {
        path: toRelativePath(PROTECTED_ROUTES.DASHBOARD),
        element: (
          <PageWithErrorBoundary pageName="Dashboard">
            <DashboardPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(PROTECTED_ROUTES.REPORTS),
        element: (
          <PageWithErrorBoundary pageName="Reports">
            <ReportsPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(PROTECTED_ROUTES.CALENDAR),
        element: (
          <PageWithErrorBoundary pageName="Calendar">
            <CalendarPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(PROTECTED_ROUTES.NOTIFICATIONS),
        element: (
          <PageWithErrorBoundary pageName="Notifications">
            <NotificationsPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        // No sidebar slot: reached from the dashboard's friend-activity module,
        // which is what makes it a subpage of the dashboard rather than a
        // destination of its own.
        path: toRelativePath(PROTECTED_ROUTES.FEED),
        element: (
          <PageWithErrorBoundary pageName="Feed">
            <FeedPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        // No sidebar slot: reached from the dashboard's Your Standing panel,
        // which is what makes it a subpage of the dashboard rather than a
        // destination of its own — the same shape as the feed.
        path: toRelativePath(PROTECTED_ROUTES.STANDING),
        element: (
          <PageWithErrorBoundary pageName="Standing">
            <StandingPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(PROTECTED_ROUTES.FRIENDS),
        element: (
          <PageWithErrorBoundary pageName="Friends">
            <FriendsPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        // Client-only in practice, the mirror of CLIENTS_ROUTES.LIST: the
        // dropdown gates the entry on role and the endpoint returns an empty
        // list for anyone else. There is no route-level role guard in this app.
        path: toRelativePath(PROTECTED_ROUTES.TRAINERS),
        element: (
          <PageWithErrorBoundary pageName="Trainers">
            <TrainersPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(QUESTIONNAIRE_ROUTES.START),
        element: (
          <PageWithErrorBoundary pageName="Fitness Questionnaire">
            <QuestionnairePage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(QUESTIONNAIRE_ROUTES.STEP),
        element: (
          <PageWithErrorBoundary pageName="Fitness Questionnaire">
            <QuestionnairePage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(MEASUREMENTS_ROUTES.LIST),
        element: (
          <PageWithErrorBoundary pageName="Measurements">
            <MeasurementsPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        // Above LIST's siblings but after it: `/measurements/:metric` is a
        // catalogue slug, and the data router ranks the static `/measurements`
        // above it on its own.
        path: toRelativePath(MEASUREMENTS_ROUTES.METRIC),
        element: (
          <PageWithErrorBoundary pageName="Measurement Metric">
            <MeasurementMetricPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(MEASUREMENTS_ROUTES.NEW),
        element: (
          <PageWithErrorBoundary pageName="Log Measurement">
            <MeasurementFormPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        // `/measurement/new` is static, so the data router ranks it above
        // this `:id` on its own.
        path: toRelativePath(MEASUREMENTS_ROUTES.ENTRY),
        element: (
          <PageWithErrorBoundary pageName="Measurement Entry">
            <MeasurementEntryPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(MEASUREMENTS_ROUTES.EDIT),
        element: (
          <PageWithErrorBoundary pageName="Edit Measurement">
            <MeasurementFormPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(PLANS_ROUTES.LIST),
        element: (
          <PageWithErrorBoundary pageName="Plans">
            <PlansPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        // Static `/plan/new` outranks this dynamic segment in the data router,
        // so route order here is not load-bearing.
        path: toRelativePath(PLANS_ROUTES.VIEW),
        element: (
          <PageWithErrorBoundary pageName="Plan Details">
            <PlanViewPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(PLANS_ROUTES.NEW),
        element: (
          <PageWithErrorBoundary pageName="New Plan">
            <PlanFormPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(PLANS_ROUTES.EDIT),
        element: (
          <PageWithErrorBoundary pageName="Edit Plan">
            <PlanFormPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(PLANS_ROUTES.PROPOSE),
        element: (
          <PageWithErrorBoundary pageName="Propose Plan Change">
            <PlanFormPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(PLANS_ROUTES.SCHEDULE_PROPOSE),
        element: (
          <PageWithErrorBoundary pageName="Propose Plan Schedule">
            <PlanSchedulePage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(PLANS_ROUTES.SCHEDULE),
        element: (
          <PageWithErrorBoundary pageName="Plan Schedule">
            <PlanSchedulePage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(PLANS_ROUTES.SHARE),
        element: (
          <PageWithErrorBoundary pageName="Share Plan">
            <PlanSharePage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(WORKOUTS_ROUTES.LIST),
        element: (
          <PageWithErrorBoundary pageName="Workouts">
            <WorkoutsPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        // Static `/workout/new` outranks this dynamic segment in the data router,
        // so route order here is not load-bearing.
        path: toRelativePath(WORKOUTS_ROUTES.VIEW),
        element: (
          <PageWithErrorBoundary pageName="Workout Details">
            <WorkoutViewPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        // A COMPLETED session, not a workout — its own top-level noun. Sits
        // inside the authenticated shell (unlike the live session, which is
        // shell-less focus mode) because this one is read, not performed.
        path: toRelativePath(SESSIONS_ROUTES.VIEW),
        element: (
          <PageWithErrorBoundary pageName="Session Summary">
            <SessionSummaryPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        // Nested under the session rather than the workout: it takes a
        // COMPLETED-workout hashid, and it is owner-only where the summary
        // above also admits the plan's trainer.
        path: toRelativePath(SESSIONS_ROUTES.COMPARE),
        element: (
          <PageWithErrorBoundary pageName="Session Comparison">
            <SessionComparisonPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(WORKOUTS_ROUTES.NEW),
        element: (
          <PageWithErrorBoundary pageName="New Workout">
            <WorkoutFormPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(WORKOUTS_ROUTES.EDIT),
        element: (
          <PageWithErrorBoundary pageName="Edit Workout">
            <WorkoutFormPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(EXERCISES_ROUTES.LIST),
        element: (
          <PageWithErrorBoundary pageName="Exercise Library">
            <ExercisesPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        // Static `/exercise/new` outranks this dynamic segment in the data
        // router, so route order here is not load-bearing.
        path: toRelativePath(EXERCISES_ROUTES.VIEW),
        element: (
          <PageWithErrorBoundary pageName="Exercise Details">
            <ExerciseViewPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(EXERCISES_ROUTES.NEW),
        element: (
          <PageWithErrorBoundary pageName="New Exercise">
            <CustomExerciseFormPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(EXERCISES_ROUTES.EDIT),
        element: (
          <PageWithErrorBoundary pageName="Edit Exercise">
            <CustomExerciseFormPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(GOALS_ROUTES.NEW),
        element: (
          <PageWithErrorBoundary pageName="New Goal">
            <GoalFormPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(GOALS_ROUTES.EDIT),
        element: (
          <PageWithErrorBoundary pageName="Edit Goal">
            <GoalFormPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(GOALS_ROUTES.VIEW),
        element: (
          <PageWithErrorBoundary pageName="Goal Details">
            <GoalViewPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(GOALS_ROUTES.LIST),
        element: (
          <PageWithErrorBoundary pageName="Goals">
            <GoalsPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(CHALLENGES_ROUTES.LIST),
        element: (
          <PageWithErrorBoundary pageName="Challenges">
            <ChallengesPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        // Trainer-only in practice — the sidebar, tab bar and global search all
        // gate the entry on role, and the endpoint 403s for a client. There is
        // no route-level role guard in this app to hang it off.
        path: toRelativePath(CLIENTS_ROUTES.LIST),
        element: (
          <PageWithErrorBoundary pageName="Clients">
            <ClientsPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        // One client, as their trainer. Trainer-only in practice and for the
        // same reason as the roster: every read behind it 403s for anybody
        // else, and there is no route-level role guard in this app to hang it
        // off. Registered after the list so `/clients` cannot be shadowed.
        path: toRelativePath(CLIENTS_ROUTES.VIEW),
        element: (
          <PageWithErrorBoundary pageName="Client">
            <ClientViewPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        // One of the client's measurement entries, read-only. Gated like the
        // client page: the entry endpoint 403s unless their trainer may read
        // their measurements.
        path: toRelativePath(CLIENTS_ROUTES.MEASUREMENT),
        element: (
          <PageWithErrorBoundary pageName="Client Measurement">
            <MeasurementEntryPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        // One of the client's body metrics, read-only. Gated like the client
        // page: the metric endpoint refuses unless the client shares their
        // measurements with their trainer.
        path: toRelativePath(CLIENTS_ROUTES.METRIC),
        element: (
          <PageWithErrorBoundary pageName="Client Body Metric">
            <MeasurementMetricPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        // The client's side, and ungated: answering a change to your own
        // record is not something a trainer's subscription decides.
        path: toRelativePath(PROPOSALS_ROUTES.VIEW),
        element: (
          <PageWithErrorBoundary pageName="Change Proposal">
            <ChangeProposalPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(CHALLENGES_ROUTES.NEW),
        element: (
          <PageWithErrorBoundary pageName="New Challenge">
            <ChallengeFormPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(CHALLENGES_ROUTES.EDIT),
        element: (
          <PageWithErrorBoundary pageName="Edit Challenge">
            <ChallengeFormPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(CHALLENGES_ROUTES.VIEW),
        element: (
          <PageWithErrorBoundary pageName="Challenge">
            <ChallengeViewPage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(PROFILE_ROUTES.SELF),
        element: (
          <PageWithErrorBoundary pageName="Profile">
            <ProfilePage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(PROFILE_ROUTES.VIEW),
        element: (
          <PageWithErrorBoundary pageName="Profile">
            <ProfilePage />
          </PageWithErrorBoundary>
        ),
      },
      {
        path: toRelativePath(PROTECTED_ROUTES.SETTINGS),
        element: (
          <PageWithErrorBoundary pageName="Settings">
            <SettingsPage />
          </PageWithErrorBoundary>
        ),
      },
    ],
  },
  // Catch-all route for 404
  {
    path: '*',
    element: (
      <PageWithErrorBoundary pageName="404 - Page Not Found">
        <NotFoundPage />
      </PageWithErrorBoundary>
    ),
  },
]);

// AppRouter component
export const AppRouter: React.FC = () => {
  return <RouterProvider router={router} />;
};

export default AppRouter;
